import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { Container, Loading, ErrorMessage, SimpleButton } from '@/src/ui';
import { apiRequest } from '@/src/services';
import type { ExamSummaryDTO } from './DTOs';

const ExamResultsPage: React.FC = () => {
    document.title = 'Exam Summary';

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [summaryData, setSummaryData] = useState<ExamSummaryDTO | null>(null);

    const { attemptId = '0' } = useParams();

    const navigate = useNavigate();

    useEffect(() => {
        (async function init() {
            try {
                const res = await apiRequest<ExamSummaryDTO>(
                    'get',
                    `assessments/studentExamView/results/${attemptId}`
                );
                const data = res.data;

                const formattedDate = new Intl.DateTimeFormat('en-US', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                }).format(new Date(data.completionDate));
                data.completionDate = formattedDate;

                setSummaryData(data);
            } catch {
                setError('Failed to load navigation data.');
            } finally {
                setLoading(false);
            }
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Container className="user-min-font-size">
            <h1>Congratulations!</h1>
            <div className="mt-12">Your exam was submitted. Here are your results:</div>

            <br />

            {loading && <Loading size="lg" />}
            <ErrorMessage error={error} />
            {summaryData && (
                <div>
                    <div>Completion Date: {summaryData.completionDate.toString()}</div>
                    <div>
                        Correct Answers: {summaryData.correctCount} / {summaryData.totalCount}
                    </div>
                    <div>Under Review: {summaryData.underReviewCount}</div>
                </div>
            )}

            <br />

            <div>Click Exit when you are ready to leave this page.</div>

            <br />

            <div className="flex flex-row gap-4">
                <div>
                    <SimpleButton
                        sx={btnSxBlue}
                        onClick={() => navigate(`/exams/viewExamAttempts`, { replace: true })}
                        disabled={loading}
                    >
                        Exit
                    </SimpleButton>
                </div>
            </div>
        </Container>
    );
};

const btnSxBlue = {
    '&&': {
        // bumps specificity
        backgroundColor: '#20a2dc',
        color: '#fff',
        '&:hover': { backgroundColor: '#178ec2' },
        '&:disabled': {
            // keep your disabled look here or leave for your global override
            backgroundColor: '#fafafa',
            color: '#aaa',
        },
    },
};

export default ExamResultsPage;
